import { useEffect, useState } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import { useHistory, useLocation } from 'react-router-dom';

import { useGetMagicLink } from '../../root/useGetMagicLink';
import routes from '../../routes/routes.constants';
import { useTimer } from '../../utils/hooks/useTimer';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

import { loginChannel, useLogin } from './useLogin';

export const useExternalMagicLink = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const [isSignedOut, setIsSignedOut] = useState(!authState?.isAuthenticated);
  const [isFetchingTokens, setIsFetchingTokens] = useState(true);
  const [isErrorLoggingIn, setIsErrorLoggingIn] = useState(false);
  const { isSessionLoading, tokens, isTokensPresent, redirect } =
    useGetMagicLink();
  const { success } = useLogin();
  const { start, getTimePassed } = useTimer();
  const history = useHistory();
  const location = useLocation();
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    const signOut = async () => {
      start();
      oktaAuth.closeSession().finally(() => {
        setIsSignedOut(true);
        trackEvent(mixpanelEvents.magicLinkClosedSession, {
          timeToLogout: getTimePassed(),
        });
      });
    };
    if (location.pathname === routes.MAGIC && !isSignedOut) {
      signOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== routes.MAGIC) {
      setIsFetchingTokens(false);
      return;
    }
    if (tokens === null || isSessionLoading || !isSignedOut) {
      return;
    }
    if ((!tokens.accessToken || !tokens.idToken) && !isTokensPresent) {
      setIsFetchingTokens(false);
      return;
    }
    if (!isTokensPresent) {
      oktaAuth.tokenManager.setTokens({
        accessToken: tokens.accessToken,
        idToken: tokens.idToken,
        refreshToken: tokens.refreshToken,
      });
    }
    if (isErrorLoggingIn) {
      trackEvent(mixpanelEvents.magicLinkRedirectToLogin, {
        cause: 'error logging in',
      });
      history.push(routes.LOGIN);
    }
    let idTokenString: string;
    if (isTokensPresent) {
      idTokenString = oktaAuth.getIdToken()!;
    } else {
      idTokenString = tokens.idToken?.idToken!;
    }
    success(idTokenString, redirect)
      .catch(() => setIsErrorLoggingIn(true))
      .finally(() => {
        setIsFetchingTokens(false);
        // https://developer.okta.com/docs/guides/session-cookie/main/#retrieve-a-session-cookie-by-visiting-a-session-redirect-link
        if (tokens.sessionToken) {
          oktaAuth.session.setCookieAndRedirect(tokens.sessionToken);
        }
      });

    loginChannel.postMessage('login');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSessionLoading,
    tokens,
    redirect,
    isErrorLoggingIn,
    location.pathname,
    isSignedOut,
  ]);

  return { isFetchingTokens, tokens };
};
